import { Box, SegmentedControl, Skeleton, Table, Text, Title } from "@mantine/core";
import dayjs from "dayjs";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import useSwr from "swr";
import { DashboardApi, DashboardARApi } from "../../apis";
import type { IAuth } from "../../interfaces/IAuth";
import type { IGeneral } from "../../interfaces/IGeneral";
import type { IReference } from "../../interfaces/IReference";
import { currencyFormat } from "../../utils";
import { renderBusinessName } from "../../utils/render-business-name";
import { Page, PageHeader } from "../../components/layout/Page";
import classes from "./Dashboard.module.css";

/** @deprecated Kept for legacy chart components; dashboard now uses PeriodDays windows. */
export interface IFilter {
  date: string;
  pendingStartDate: string | null;
  pendingEndDate: string | null;
  confirmedStartDate: string | null;
  confirmedEndDate: string | null;
  categoryId: string | null;
}

type PeriodDays = 7 | 30 | 90;

type AgingRow = {
  key: string;
  label: string;
  count: number;
  amount: number;
  color: string;
};

function readCssVar(name: string, fallback: string) {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

function buildWindow(days: PeriodDays, end = dayjs()) {
  const endDay = end.startOf("day");
  const startDay = endDay.subtract(days - 1, "day");
  return {
    start: startDay,
    end: endDay,
    startIso: startDay.format("YYYY-MM-DD"),
    endIso: endDay.format("YYYY-MM-DD"),
  };
}

/** Convert month-to-date cumulative series into per-day deltas (handles month reset). */
function dailyDeltas(rows: { confirmedAmount?: number; paidAmount?: number }[], field: "confirmedAmount" | "paidAmount") {
  return rows.map((r, i) => {
    const v = Number(r[field] || 0);
    if (i === 0) return v;
    const prev = Number(rows[i - 1][field] || 0);
    if (v < prev) return v;
    return v - prev;
  });
}

function formatMoney(n: number) {
  return currencyFormat(n);
}

function formatMoneyPlain(n: number) {
  return currencyFormat(n, { withSymbol: false });
}

function statusPillClass(code: string) {
  if (code === "CONFIRMED" || code === "APPROVED") return classes.statusConfirmed;
  if (code === "CANCELED" || code === "CANCELLED" || code === "REJECTED") return classes.statusCanceled;
  if (code === "CLOSED") return classes.statusPaid;
  if (code === "SENT" || code === "VIEWED") return classes.statusSent;
  return classes.statusDefault;
}

export function Dashboard() {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const user = useSelector((state: { auth: IAuth }) => state.auth.user);
  const isBuyer = useSelector((state: { auth: IAuth }) => state.auth.myBusinessType) === "BUYER";
  // Buyer sees the same figures as payables: own invoices live under /buyer-invoice.
  const invoiceBase = isBuyer ? "/buyer-invoice" : "/sales-invoice";
  const invoiceStatus = useSelector((state: { general: IGeneral }) => state.general.invoiceStatus) as
    | IReference[]
    | undefined;

  const [periodDays, setPeriodDays] = useState<PeriodDays>(30);
  const window = useMemo(() => buildWindow(periodDays), [periodDays]);
  const prevWindow = useMemo(() => {
    const end = window.start.subtract(1, "day");
    return buildWindow(periodDays, end);
  }, [periodDays, window.start]);

  const businessName =
    renderBusinessName(user?.currentBusiness as any) ||
    (user?.currentBusiness as any)?.profileName ||
    (user?.currentBusiness as any)?.partner?.businessName ||
    "-";

  const mainFilter = useMemo(
    () => ({
      date: window.endIso,
      confirmedStartDate: window.startIso,
      confirmedEndDate: window.endIso,
      pendingStartDate: window.startIso,
      pendingEndDate: window.endIso,
      categoryId: null as string | null,
    }),
    [window.endIso, window.startIso],
  );

  const prevFilter = useMemo(
    () => ({
      date: prevWindow.endIso,
      confirmedStartDate: prevWindow.startIso,
      confirmedEndDate: prevWindow.endIso,
      pendingStartDate: prevWindow.startIso,
      pendingEndDate: prevWindow.endIso,
      categoryId: null as string | null,
    }),
    [prevWindow.endIso, prevWindow.startIso],
  );

  const { data: dataMain, isLoading } = useSwr(
    `/inv/dashboard/main?${JSON.stringify(mainFilter)}`,
    () => DashboardApi.main(mainFilter),
  );

  const { data: dataPrev } = useSwr(
    `/inv/dashboard/main/prev?${JSON.stringify(prevFilter)}`,
    () => DashboardApi.main(prevFilter),
    { revalidateOnFocus: false },
  );

  const { data: arSummary } = useSwr("swr.dashboard.ar-summary", () => DashboardARApi.getARSummary(), {
    revalidateOnFocus: false,
  });

  const { data: recentInvoices = [] } = useSwr<any[]>(
    "swr.dashboard.recent-invoices",
    () => DashboardApi.recentInvoices(),
    { revalidateOnFocus: false },
  );

  const chartRows = dataMain?.confirmedAmounts ?? [];
  const confirmedDaily = useMemo(() => dailyDeltas(chartRows, "confirmedAmount"), [chartRows]);
  const paidDaily = useMemo(() => dailyDeltas(chartRows, "paidAmount"), [chartRows]);
  const confirmedSum = confirmedDaily.reduce((s, n) => s + n, 0);
  const paidSum = paidDaily.reduce((s, n) => s + n, 0);

  const prevRows = dataPrev?.confirmedAmounts ?? [];
  const prevConfirmedSum = useMemo(
    () => dailyDeltas(prevRows, "confirmedAmount").reduce((s, n) => s + n, 0),
    [prevRows],
  );
  const prevPaidSum = useMemo(() => dailyDeltas(prevRows, "paidAmount").reduce((s, n) => s + n, 0), [prevRows]);

  const pendingSnap = Number((arSummary as any)?.totalReceivables?.onTimeAmount ?? 0);
  const overdueSnap = Number((arSummary as any)?.totalReceivables?.overdueAmount ?? 0);
  const due7Amount = Number((arSummary as any)?.upcoming?.next7Days?.amount ?? 0);
  const due7Count = Number((arSummary as any)?.upcoming?.next7Days?.invoiceCount ?? 0);

  const periodLabel =
    periodDays === 7 ? t("period.d7") : periodDays === 90 ? t("period.d90") : t("period.d30");

  const chartHasActivity = confirmedSum > 0 || paidSum > 0;

  const colors = useMemo(
    () => ({
      confirmed: readCssVar("--mantine-color-gray-8", "#334155"),
      paid: readCssVar("--mantine-color-green-6", "#10b981"),
      grid: readCssVar("--mantine-color-gray-1", "#f1f5f9"),
      label: readCssVar("--mantine-color-gray-6", "#64748b"),
      tooltipBg: readCssVar("--mantine-color-white", "#ffffff"),
      tooltipBorder: readCssVar("--mantine-color-gray-2", "#e2e8f0"),
      text: readCssVar("--mantine-color-gray-9", "#0f172a"),
    }),
    [dataMain],
  );

  const categories = chartRows.map((d) => d.date);
  const labelStep = Math.max(1, Math.ceil(categories.length / 5));

  const comboOpts = useMemo(
    () => ({
      chart: {
        type: "column",
        height: 200,
        backgroundColor: "transparent",
        spacing: [8, 4, 4, 4],
        style: { fontFamily: "inherit" },
      },
      title: { text: undefined },
      credits: { enabled: false },
      legend: { enabled: false },
      xAxis: {
        categories,
        lineColor: colors.grid,
        tickLength: 0,
        labels: {
          style: { color: colors.label, fontSize: "14px" },
          step: labelStep,
        },
      },
      yAxis: {
        title: { text: undefined },
        gridLineColor: colors.grid,
        lineWidth: 0,
        labels: { enabled: false },
      },
      tooltip: {
        shared: true,
        backgroundColor: colors.tooltipBg,
        borderColor: colors.tooltipBorder,
        borderRadius: 8,
        style: { fontSize: "14px", fontFamily: "inherit", color: colors.text },
        formatter: function (this: any) {
          const pts = (this.points || []) as { series: { name: string }; y?: number }[];
          const head = `<b>${this.x}</b>`;
          const lines = pts
            .map((p) => `${p.series.name}: <b>${formatMoney(Number(p.y || 0))}</b>`)
            .join("<br/>");
          return `${head}<br/>${lines}`;
        },
      },
      plotOptions: {
        column: {
          borderRadius: 2,
          pointPadding: 0.08,
          groupPadding: 0.12,
          borderWidth: 0,
        },
      },
      series: [
        {
          name: t("chart.confirmed"),
          type: "column" as const,
          color: colors.confirmed,
          data: confirmedDaily,
        },
        {
          name: t("chart.paid"),
          type: "column" as const,
          color: colors.paid,
          data: paidDaily,
        },
      ],
      accessibility: { enabled: false },
    }),
    [categories.join(","), confirmedDaily.join(","), paidDaily.join(","), colors, labelStep, t],
  );

  /** Map AGING_BUCKET (UB paymentDate) → canvas 5 buckets. */
  const agingRows: AgingRow[] = useMemo(() => {
    const buckets = (arSummary as any)?.agingBuckets;
    const pick = (key: string) => ({
      count: Number(buckets?.[key]?.count ?? 0),
      amount: Number(buckets?.[key]?.total ?? 0),
    });

    // Fallback: legacy overdueStats merge (DEV without agingBuckets yet)
    if (!buckets) {
      const stats = dataMain?.overdueStats ?? [];
      const byName = (re: RegExp) => stats.filter((s) => re.test(s.name || ""));
      const sum = (rows: typeof stats) => ({
        count: rows.reduce((a, r) => a + (r.invoiceCount || 0), 0),
        amount: rows.reduce((a, r) => a + (r.amountToPay || 0), 0),
      });
      return [
        { key: "current", label: t("aging.current"), ...sum(byName(/хэвийн|normal/i)), color: "var(--mantine-color-green-6)" },
        { key: "d1", label: t("aging.d1_30"), ...sum(byName(/1-3|4-30|1–30|1-30/i)), color: "var(--mantine-color-yellow-5)" },
        { key: "d31", label: t("aging.d31_60"), ...sum(byName(/31-60|31–60/i)), color: "var(--mantine-color-yellow-6)" },
        { key: "d61", label: t("aging.d61_90"), ...sum(byName(/61-90|61–90/i)), color: "var(--mantine-color-red-6)" },
        { key: "d91", label: t("aging.d91"), ...sum(byName(/91|over/i)), color: "var(--mantine-color-red-9)" },
      ];
    }

    const current = pick("CURRENT");
    const d1 = pick("DAYS_1_30");
    const d31 = pick("DAYS_31_60");
    const d61 = pick("DAYS_61_90");
    const d91 = pick("DAYS_91_PLUS");

    return [
      { key: "current", label: t("aging.current"), count: current.count, amount: current.amount, color: "var(--mantine-color-green-6)" },
      { key: "d1", label: t("aging.d1_30"), count: d1.count, amount: d1.amount, color: "var(--mantine-color-yellow-5)" },
      { key: "d31", label: t("aging.d31_60"), count: d31.count, amount: d31.amount, color: "var(--mantine-color-yellow-6)" },
      { key: "d61", label: t("aging.d61_90"), count: d61.count, amount: d61.amount, color: "var(--mantine-color-red-6)" },
      { key: "d91", label: t("aging.d91"), count: d91.count, amount: d91.amount, color: "var(--mantine-color-red-9)" },
    ];
  }, [arSummary, dataMain?.overdueStats, t]);

  const agingTotal = agingRows.reduce((s, r) => s + r.amount, 0);
  const over91 = agingRows.find((r) => r.key === "d91");

  const resolveStatusLabel = (code: string) => {
    const fromRef = invoiceStatus?.find((x) => x.code === code)?.name;
    if (fromRef) return fromRef;
    return t(`status.${code}`, { defaultValue: code });
  };

  const recentRows = (recentInvoices ?? []).slice(0, 5);
  const topDebtors = (dataMain?.highestRemainder ?? []).slice(0, 3);
  const sentUnconfirmed = {
    count: Number((arSummary as any)?.sentUnconfirmed?.invoiceCount ?? 0),
    amount: Number((arSummary as any)?.sentUnconfirmed?.amount ?? 0),
  };

  const kpis = [
    {
      key: "pending",
      label: t("kpi.pending"),
      value: pendingSnap,
      def: t("kpi.pendingDef"),
      valueClass: undefined as string | undefined,
      prev: null as number | null,    },
    {
      key: "paid",
      label: t("kpi.paid"),
      value: paidSum,
      def: t("kpi.paidDef"),
      valueClass: classes.kpiValuePaid,
      prev: dataPrev ? prevPaidSum : null,    },
    {
      key: "overdue",
      label: t("kpi.overdue"),
      value: overdueSnap,
      def: `${t("kpi.overdueDef")} · ${t("kpi.overdueDefSnapshot")}`,
      valueClass: undefined as string | undefined,
      prev: null as number | null,    },
    {
      key: "confirmed",
      label: t("kpi.confirmed"),
      value: confirmedSum,
      def: t("kpi.confirmedDef"),
      valueClass: undefined as string | undefined,
      prev: dataPrev ? prevConfirmedSum : null,    },
  ];

  return (
    <Page>
      <PageHeader
        title={t("title")}
        subtitle={t("subtitle", {
          start: window.start.format("YYYY-MM-DD"),
          end: window.end.format("YYYY-MM-DD"),
          business: businessName,
        })}
        extra={
          <SegmentedControl
            className={classes.periodSeg}
            value={String(periodDays)}
            onChange={(v) => setPeriodDays(Number(v) as PeriodDays)}
            data={[
              { label: t("period.d7"), value: "7" },
              { label: t("period.d30"), value: "30" },
              { label: t("period.d90"), value: "90" },
            ]}
          />
        }
      />

      <Box className={classes.kpiRow}>
        {isLoading
          ? [1, 2, 3, 4].map((i) => <Skeleton key={i} h={120} radius="lg" />)
          : kpis.map((k) => {
              const showPrev = k.prev != null && k.prev !== 0 && k.value !== k.prev;
              // DeHUB UI стандарт: улаан зөвхөн сөрөг дүнд. Өсөлт/бууралтыг өнгөөр биш, тоогоор нь уншина.
              const prevClass = showPrev && (k.prev as number) < 0 ? classes.kpiPrevBad : undefined;
              const valueClass = k.value < 0 ? classes.kpiValueOverdue : k.valueClass;
              return (
                <Box key={k.key} className={classes.kpiCard}>
                  <Text className={classes.kpiLabel}>{k.label}</Text>
                  <Text className={`${classes.kpiValue} ${valueClass || ""}`}>{formatMoney(k.value)}</Text>
                  <Text className={classes.kpiDef}>{k.def}</Text>
                  {showPrev && (
                    <Text className={`${classes.kpiPrev} ${prevClass || ""}`}>
                      {t("kpi.prev", { period: periodLabel, value: formatMoney(k.prev as number) })}
                    </Text>
                  )}
                </Box>
              );
            })}
      </Box>

      <Box className={classes.row2}>
        <Box className={`${classes.card} ${classes.span2}`}>
          <Box className={classes.cardHead}>
            <Title order={2} className={classes.cardTitle}>
              {t("chart.title")}
            </Title>
            <Box className={classes.legend}>
              <Text span className={classes.legendItem}>
                <Box component="span" className={classes.legendSwatch} style={{ background: colors.confirmed }} />
                {t("chart.confirmed")}
              </Text>
              <Text span className={classes.legendItem}>
                <Box component="span" className={classes.legendSwatch} style={{ background: colors.paid }} />
                {t("chart.paid")}
              </Text>
            </Box>
          </Box>
          {isLoading ? (
            <Skeleton h={200} radius="md" />
          ) : !chartHasActivity ? (
            <Box className={classes.chartEmpty}>{t("chart.empty")}</Box>
          ) : (
            <Box className={classes.chartWrap}>
              <HighchartsReact highcharts={Highcharts} options={comboOpts} />
            </Box>
          )}
        </Box>

        <Box className={classes.card}>
          <Box className={classes.cardHead}>
            <Title order={2} className={classes.cardTitle}>
              {t(isBuyer ? "aging.titleBuyer" : "aging.title")}
            </Title>
            {!isBuyer && (
              <Link to="/ledger/reports/ar-aging" className={classes.cardLink}>
                {t("aging.report")}
              </Link>
            )}
          </Box>
          <Text className={classes.agingTotal}>
            {t("aging.total", { amount: formatMoney(agingTotal) })}
          </Text>
          <Box className={classes.agingBar} data-empty={agingTotal <= 0 || undefined} aria-hidden>
            {agingRows.map((row) =>
              row.amount > 0 ? (
                <Box
                  key={row.key}
                  className={classes.agingSeg}
                  style={{
                    flexGrow: Math.max(row.amount, 1),
                    background: row.color,
                  }}
                />
              ) : null,
            )}
          </Box>
          <Box className={classes.agingRows}>
            {agingRows.map((row) => (
              <Box key={row.key} className={classes.agingRow}>
                <Text span className={classes.agingLeft}>
                  <Box component="span" className={classes.agingDot} style={{ background: row.color }} />
                  {row.label}
                </Text>
                <Box className={classes.agingRight}>
                  <Text span className={classes.agingCount}>
                    {t("aging.count", { count: row.count })}
                  </Text>
                  <Text span className={classes.agingAmount}>
                    {formatMoney(row.amount)}
                  </Text>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box className={classes.row3}>
        <Box className={`${classes.card} ${classes.cardFlush} ${classes.span2}`}>
          <Box className={`${classes.cardHead} ${classes.cardHeadFlush}`}>
            <Title order={2} className={classes.cardTitle}>
              {t("recent.title")}
            </Title>
            <Link to={invoiceBase} className={classes.cardLink}>
              {t("recent.all")}
            </Link>
          </Box>
          {recentRows.length === 0 ? (
            <Text className={classes.tblEmpty}>{t("recent.empty")}</Text>
          ) : (
            <Box className={classes.tblWrap}>
            <Table className={classes.tbl} withRowBorders={false} horizontalSpacing={0} verticalSpacing={0}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th scope="col">{t("recent.colRef")}</Table.Th>
                  <Table.Th scope="col">{t(isBuyer ? "recent.colPartnerBuyer" : "recent.colPartner")}</Table.Th>
                  <Table.Th scope="col">{t("recent.colStatus")}</Table.Th>
                  <Table.Th scope="col">{t("recent.colDate")}</Table.Th>
                  <Table.Th scope="col">{t("recent.colAmount")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {recentRows.map((row: any, idx: number) => {
                  const code = String(row.status || "");
                  const ref = String(row.refCode || "").replace(/^#/, "");
                  const dateStr = row.date ? dayjs(row.date).format("YYYY-MM-DD") : "-";
                  return (
                    <Table.Tr
                      key={`${ref}-${idx}`}
                      onClick={() => {
                        if (row.id) navigate(`${invoiceBase}/${row.id}/details`);
                        else if (ref) navigate(`${invoiceBase}?q=${encodeURIComponent(ref)}`);
                      }}
                    >
                      <Table.Td className={classes.refMono}>{ref || "-"}</Table.Td>
                      <Table.Td className={classes.partnerCell}>{row.business || "-"}</Table.Td>
                      <Table.Td>
                        <span className={`${classes.statusPill} ${statusPillClass(code)}`}>
                          {resolveStatusLabel(code)}
                        </span>
                      </Table.Td>
                      <Table.Td className={classes.dateCell}>{dateStr}</Table.Td>
                      <Table.Td className={classes.amountCell}>{formatMoneyPlain(Number(row.amount || 0))}</Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
            </Box>
          )}
        </Box>

        <Box className={classes.sideStack}>
          <Box className={classes.card}>
            <Title order={2} className={classes.cardTitle}>
              {t("attention.title")}
            </Title>
            <Box className={classes.attnList}>
              {over91 && over91.count > 0 && (
                <button
                  type="button"
                  className={`${classes.attnRow} ${classes.attnRowDanger}`}
                  onClick={() => navigate(`${invoiceBase}?status=CONFIRMED`)}
                >
                  <Box className={classes.attnLeft}>
                    <Text className={`${classes.attnTitle} ${classes.attnTitleDanger}`}>{t("attention.over91")}</Text>
                    <Text className={classes.attnMeta}>{t("attention.over91Count", { count: over91.count })}</Text>
                  </Box>
                  <Text className={classes.attnAmount}>{formatMoney(over91.amount)}</Text>
                </button>
              )}

              <button
                type="button"
                className={classes.attnRow}
                onClick={() => navigate(`${invoiceBase}?status=CONFIRMED`)}
              >
                <Box className={classes.attnLeft}>
                  <Text className={classes.attnTitle}>{t("attention.due7")}</Text>
                  <Text className={classes.attnMeta}>{t("attention.due7Count", { count: due7Count })}</Text>
                </Box>
                <Text className={classes.attnAmount}>{formatMoney(due7Amount)}</Text>
              </button>

              {sentUnconfirmed.count > 0 && (
                <button
                  type="button"
                  className={classes.attnRow}
                  onClick={() => navigate(`${invoiceBase}?status=SENT`)}
                >
                  <Box className={classes.attnLeft}>
                    <Text className={classes.attnTitle}>{t(isBuyer ? "attention.unconfirmedBuyer" : "attention.unconfirmed")}</Text>
                    <Text className={classes.attnMeta}>
                      {t(isBuyer ? "attention.unconfirmedHintBuyer" : "attention.unconfirmedHint")} · {t("attention.unconfirmedCount", { count: sentUnconfirmed.count })}
                    </Text>
                  </Box>
                  <Text className={classes.attnAmount}>{formatMoney(sentUnconfirmed.amount)}</Text>
                </button>
              )}
            </Box>
          </Box>

          {topDebtors.length > 0 && (
            <Box className={classes.card}>
              <Title order={2} className={classes.cardTitle}>
                {t(isBuyer ? "topDebtors.titleBuyer" : "topDebtors.title")}
              </Title>
              <Box className={classes.debtorList}>
                {topDebtors.map((b: any) => (
                  <button
                    type="button"
                    key={b.id || b.refCode}
                    className={classes.debtorRow}
                    onClick={() => navigate(`/customer-billing?biz=${encodeURIComponent(b.id)}`)}
                  >
                    <Box>
                      <Text className={classes.debtorName}>{renderBusinessName(b) || b.profileName || "-"}</Text>
                      <Text className={classes.debtorMeta}>
                        {t("topDebtors.invoiceCount", { count: b.invoiceCount || 0 })}
                      </Text>
                    </Box>
                    <Text className={classes.attnAmount}>{formatMoney(Number(b.amountToPay || 0))}</Text>
                  </button>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Page>
  );
}
